import type { AccountDrawerProps } from './components/account-drawer';

import { paths } from 'src/routes/paths';

import { Iconify } from 'src/components/iconify';

// =============================================================================
// Account Drawer Nav Config
// =============================================================================
// Defines the menu items shown in the account drawer that slides out when
// the user clicks the avatar in the top-right corner of the dashboard.
//
// How it works:
//   1. User clicks the avatar → AccountButton (account-button.tsx) fires onOpen
//   2. AccountDrawer (account-drawer.tsx) renders with this `_account` data
//   3. Each item renders as a link using RouterLink
//
// To add a new item:
//   - Add an entry here with label, href (use paths.xxx.root), and an icon
//   - Make sure the route is registered in src/routes/sections/
//   - Use href: '#' for items that aren't built yet (placeholder)
//
// Currently wired:
//   Profile      → /profile (src/pages/profile/index.tsx)
//   Subscription → /billing (src/pages/billing.tsx)
//   Security     → /security (src/pages/security.tsx)
//
// Placeholders (not yet built):
//   Projects, Account settings
//
// Related files:
//   src/layouts/components/account-drawer.tsx  → renders this data
//   src/layouts/components/account-button.tsx  → the avatar button that opens it
//   src/layouts/dashboard/layout.tsx           → mounts AccountDrawer with _account
//   src/routes/paths.ts                        → path constants used for hrefs
// =============================================================================

export const _account: AccountDrawerProps['data'] = [
  {
    label: 'Profile',
    href: paths.profile.root,
    icon: <Iconify icon="custom:profile-duotone" />,
  },
  {
    // Labeled "Subscription" here (what the user is managing) even though
    // the page itself is titled "Billing" (src/pages/billing.tsx's
    // `metadata.title`) and its route/section/section-component are all
    // named `billing*`/`Billing*` throughout (paths.billing.root,
    // billingRoutes, BillingView). Not an inconsistency to reconcile —
    // it's the same kind of "internal name vs. user-facing label" split
    // as the other entries here (e.g. "Account settings" links to '#'
    // while everything behind it would be named differently); the nav
    // label is chosen for what reads best to an agent glancing at this
    // drawer, independent of what the underlying code calls the feature.
    label: 'Subscription',
    href: paths.billing.root,
    icon: <Iconify icon="custom:invoice-duotone" />,
  },
  { label: 'Security', href: '/security', icon: <Iconify icon="solar:shield-keyhole-bold-duotone" /> },
  { label: 'Account settings', href: '#', icon: <Iconify icon="solar:settings-bold-duotone" /> },
];
